/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger, ModalService } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { SearchService } from '@app/ui/search/search.service';
import { DiService } from '@app/di/di.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { AppConfigService } from '@app/common/app-config.service';
import { UtilService } from '@app/util/util.service';
import { ExternalAppContextService } from '@app/di/external-app-context.service';
import { DiConfigService } from '@app/di/di-config.service';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { NotifyService } from '@app/common/notify.service';
import { MessageService } from '@core/lib/services/message.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { appConstants } from '@app/common/app-constants';
import { TabContextService } from '@app/ui/client-cache/tab-context.service';
import { TranslateService } from '@ngx-translate/core';
import { ExamCacheService } from '@app/ui/client-cache/exam-cache.service';
import { TabWorkListService } from '@app/ui/tabs/tab-work-list.service';
import { ReportContextService } from '@app/di/integrations/reporting/report-context.service';
import { ReportSupportService } from '@app/di/integrations/reporting/report-support.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { SignOutService } from '@app/common/sign-out.service';
import { REPORT_STATUS, REPORTING_EVENT } from '@app/di/integrations/reporting/reporting-contants';
import { Subscription } from 'rxjs';
import { SearchCriteriaType, SearchParams } from '@app/common/types/search-params';
import { DatepickerRangeOptions } from '@wfm/core';
import {
  PerformanceMonitorService,
  PerformanceSource
} from '@app/ui/masthead/performance-monitor/performance-monitor.service';

const log = new Logger('PsReportingService');

@Injectable({
  providedIn: 'root'
})
export class PsReportingService {
  private isNotify: boolean;
  private subscribers: Subscription[];
  private REPORTING_CONFIG;
  private commaDelimiter = ',';
  private closeViewerCallback;
  private checkViewerContextCallback;
  private callBackOnViewerClose;

  // Parameters to handle report open/cancel failure
  private saveFailedAccession = null;
  private cancelFailedAccession = null;
  private isSaveActionFailed = false;
  private isCancelActionFailed = false;

  // Parameters to handle signature queue scenarios
  private accessionFromSignQueue;
  private dictationStatus = appConstants.dictationIntegrationStatus.None;
  private disabledModalInstance = null;
  private openReportModalInstance;
  private differentLoginModalInstance = null;
  private modalCloseSubscription;

  // Variable to hold the report status from PowerScribe
  private reportStatusFromPS;

  // To identify addendum report
  private reportAddendumStatus;

  // To keep the cancel requested accession number
  private cancelReportAccession;
  private externalContextEnabled;
  private reportingAppProcessName;

  private REPORT_SAVED_TOPIC;
  private REPORT_AUTO_SAVED_TOPIC;
  private POWERSCRIBE_CLOSE_TOPIC;
  private REPORT_OPENNED_TOPIC;
  private REPORT_ASSOCIATED_TOPIC;
  private USER_LOGIN_TOPIC;
  private OPEN_REPORT_TOPIC;
  private PS_CHILD_WINDOW_TOPIC;
  private PS_MODAL_DIALOG_TOPIC;
  private USER_LOGGEDIN_TOPIC;

  constructor(
    private searchService: SearchService,
    private currentExamContext: CurrentExamContextService,
    private diConfigService: DiConfigService,
    private diService: DiService,
    private reportSupportService: ReportSupportService,
    private eventService: EventService,
    private messageService: MessageService,
    private integrationStatusService: IntegrationStatusService,
    private externalAppContextService: ExternalAppContextService,
    private appConfigService: AppConfigService,
    private notify: NotifyService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private tabContextService: TabContextService,
    private translate: TranslateService,
    private examCacheService: ExamCacheService,
    private tabWorklistService: TabWorkListService,
    private reportContextService: ReportContextService,
    private utilService: UtilService,
    private modalService: ModalService,
    private preferences: UserPreferenceService,
    private signOutService: SignOutService
  ) {
    this.broadcastListeners();
  }

  initialize() {
    this.externalContextEnabled = this.appConfigService.getAppConfigByKey('externalContextEnabled') === 'true';
    this.reportingAppProcessName = this.appConfigService.getAppConfigByKey('reportingAppProcessName');
    this.REPORTING_CONFIG = this.diConfigService.REPORTING_CONFIG;
    this.REPORT_SAVED_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.REPORT_SAVED';
    this.REPORT_AUTO_SAVED_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.REPORT_AUTO_SAVED';
    this.POWERSCRIBE_CLOSE_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.POWERSCRIBE_CLOSE';
    this.REPORT_OPENNED_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.REPORT_OPENNED';
    this.REPORT_ASSOCIATED_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.REPORT_ASSOCIATED';
    this.USER_LOGIN_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.USER_LOGIN';
    this.OPEN_REPORT_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.OPEN_REPORT';
    this.PS_CHILD_WINDOW_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.PS_CHILD_WINDOW';
    this.PS_MODAL_DIALOG_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.DIALOG_POPUP_RESPONSE';
    this.USER_LOGGEDIN_TOPIC =
      this.REPORTING_CONFIG.OWNERINSTANCE + '.' + this.REPORTING_CONFIG.MODULENAME + '.USER_LOGGED_IN';
    if (this.REPORTING_CONFIG.SINGLESITE === 'false') {
      this.diConfigService.getMultiSiteConfiguration().then(
        () => {
          log.info('initialize in ps-reporting-integration');
        },
        () => {
          log.error('initialize - error in retrieve SiteName mapping');
        }
      );
    }

    // Subscribe to external app remove context event
    this.externalAppContextService.removeContext$.subscribe(() => {
      this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
    });
  }

  setCloseViewerCallback(callback) {
    this.closeViewerCallback = callback;
  }

  /**
   * Method to set check viewer context callback
   */
  setCheckViewerContextCallback(callback) {
    this.checkViewerContextCallback = callback;
  }

  /**
   * Method to execute viewer closed callback function
   */
  invokeViewerClosedCallback() {
    if (!!this.callBackOnViewerClose) {
      log.info('invokeViewerClosedCallback - callBackOnViewerClose executing');
      this.callBackOnViewerClose();
      this.callBackOnViewerClose = undefined;
    }
  }

  /**
   * Method to validate configurations
   */
  isDictationConfigValid() {
    log.info("isDictationConfigValid - ModuleName :'" + this.REPORTING_CONFIG.MODULENAME + "'");
    let configStatus = true;
    if (UtilService.isEmpty(this.REPORTING_CONFIG.MODULENAME)) {
      log.error('isDictationConfigValid - Module name is empty');
      configStatus = false;
    } else if (_.isUndefined(this.REPORTING_CONFIG.SINGLESITE)) {
      log.error('isDictationConfigValid - Single site configuration undefined');
      configStatus = false;
    } else if (this.REPORTING_CONFIG.SINGLESITE === 'true' && UtilService.isEmpty(this.REPORTING_CONFIG.SITENAME)) {
      log.info(
        'isDictationConfigValid - Supported by those customers guaranteeing unique ' +
          'accession numbers, Site name empty'
      );
    } else if (
      this.REPORTING_CONFIG.SINGLESITE !== 'true' &&
      UtilService.isEmpty(this.REPORTING_CONFIG.SITENAME_KEY_LOCATION)
    ) {
      log.error('isDictationConfigValid - Site name key location is empty');
      configStatus = false;
    }

    if (!configStatus) {
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      log.error(this.translate.instant('di.desktopIntegration.critical.incorrectDictateConfig'));
    }
    return configStatus;
  }

  /**
   * Method to find study ids corresponding to accession list
   */
  getAssociationList(responseInfoParams, accNumArray, primaryId) {
    const index = accNumArray.indexOf(responseInfoParams.accessionInSaveEvent);
    if (index > -1) {
      accNumArray.splice(index, 1);
    }

    if (accNumArray.length > 0) {
      const promises = [];
      _.each(accNumArray, acc => {
        const searchParams = new SearchParams(SearchCriteriaType.ACCESSION);
        searchParams.accession = acc;
        searchParams.timeOfExam = {
          option: DatepickerRangeOptions.allDates,
          startTime: undefined,
          endTime: undefined
        };
        promises.push(this.searchService.getSearchExamsList(1, 50, searchParams));
      });

      if (promises.length > 0) {
        return new Promise((resolve, reject) => {
          Promise.all(promises).then(
            result => {
              const associationList = {};
              _.each(result, searchResult => {
                _.each(searchResult, (exam: any) => {
                  const id = exam.workItem.requestedProcedureId;
                  associationList[exam.workItem.procedureAccessionNo] = {
                    isAssociated: 'Y',
                    requestedProcedureId: id
                  };
                });
              });
              // Sync exams in database when report is saved.
              if (responseInfoParams.reportStatus > 0) {
                this.reportSupportService.syncSavedExamsInDB(
                  primaryId,
                  responseInfoParams.accessionInSaveEvent,
                  associationList
                );
              }
              resolve(associationList);
            },
            error => {
              log.error('Error occurred while searching for an associated exam, Error: ' + error);
              reject(error);
            }
          );
        });
      }
    }
  }

  /**
   * Method to handle associated exams
   */
  handleAssociatedExams(responseInfoParams, accNumArray, primaryId) {
    if (responseInfoParams.actionType === appConstants.reportActionTypes.SaveOnCloseReport) {
      log.debug('handleAssociatedExams - Save report on tab close workflow');
      this.getAssociationList(responseInfoParams, accNumArray, primaryId).then(
        data => {
          const associationIds = _.pluck(_.values(data), 'requestedProcedureId');
          if (UtilService.isNonAddendumReport(responseInfoParams)) {
            this.performActionInternal(
              'suspend',
              this.getWorkItemForAssociatedExams(primaryId, responseInfoParams.reportId, associationIds)
            );
          }
        },
        error => {
          log.error('handleAssociatedExams - error in getting association list : ' + error);
        }
      );
    } else if (!this.REPORTING_CONFIG.ASSOCIATION_ENABLED || this.utilService.isAssociationEnabled() !== true) {
      log.debug('handleAssociatedExams - Association not enabled workflow');
      this.getAssociationList(responseInfoParams, accNumArray, primaryId).then(
        data => {
          const associationList = data;
          this.readAssociatedExamsWhenAssociationNotEnabled(responseInfoParams, primaryId, associationList);
        },
        error => {
          log.error('handleAssociatedExams - error in getting association list : ' + error);
        }
      );
    } else {
      log.debug('handleAssociatedExams - Association enabled workflow');
      const associationIds = this.tabContextService.getAssociatedIds(primaryId);
      // Sync exams in database when report is saved.
      if (responseInfoParams.reportStatus > 0) {
        const associationList = this.tabContextService.getAssociatedExamList(primaryId);
        this.reportSupportService.syncSavedExamsInDB(
          primaryId,
          responseInfoParams.accessionInSaveEvent,
          associationList
        );
      }
      this.publishEvents(responseInfoParams, primaryId, associationIds);
    }
  }

  readAssociatedExamsWhenAssociationNotEnabled(responseInfoParams, primaryId, associationList) {
    const associationIds = _.pluck(_.values(associationList), 'requestedProcedureId');
    if (associationIds.length > 0) {
      this.eventService
        .sendEvent(
          new EventMessage(Events.read, new EventWorkItem(-1), {
            source: 'desktop-integration',
            associationList: associationIds
          })
        )
        .then(
          () => {
            log.debug(
              'readAssociatedExam - remote action read succeeded for' +
                ' Request ProcedureID ' +
                primaryId +
                ', Association List: ' +
                associationIds
            );
            this.tabContextService.saveAssociatedExamsInCache(primaryId, associationList);
            this.publishEvents(responseInfoParams, primaryId, associationIds);
          },
          error => {
            log.error(
              'readAssociatedExam - remote action read failed ' +
                ' with error: ' +
                error +
                ' for Requested ProcedureID: ' +
                primaryId +
                ', Association List: ' +
                associationIds
            );
          }
        );
    }
  }

  /**
   * Report saved event handler
   *
   */
  reportSavedEventHandler(contextId) {
    const tab = this.utilService.getActiveTab();
    if (tab && !tab.isExternalTab) {
      if (contextId === this.reportContextService.getCurrentRequestedProcId()) {
        this.reportContextService.removeContext(contextId);
        this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
      } else {
        log.info(
          'reportSavedEventHandler - currentContext not equal to new context - ' +
            'param contextid = ' +
            contextId +
            ' currentContext = ' +
            this.reportContextService.getCurrentRequestedProcId()
        );
        if (this.reportContextService.getCurrentRequestedProcId() === '' && !this.diService.isLogoutInitiated()) {
          log.info('reportSavedEventHandler- currentContext is null');
          // make sure the report application is minimized, not hidden
          const action =
            (tab.examContext === 'preview' || tab.examContext === 'launchreadonly') && !this.externalContextEnabled
              ? appConstants.reportActionTypes.PreviewReport
              : appConstants.reportActionTypes.Minimize;
          this.setContext(appConstants.windowState.Minimize, action, undefined);
        }
      }
    }
  }

  /**
   * reportSavedOrAutoSavedHandler - Report saved or auto saved event handler
   * save - manually save report in PowerScribe
   * auto save - save report which is already open during interrupt scenario
   */
  async reportSavedOrAutoSavedHandler(responseInfo) {
    if (this.dictationStatus !== appConstants.dictationIntegrationStatus.Disabled) {
      // Resetting the save or cancel failure on receiving the save success response
      this.isCancelActionFailed = false;
      this.isSaveActionFailed = false;
      this.cancelFailedAccession = '';
      this.saveFailedAccession = '';
      // Set the report status from response
      this.reportStatusFromPS = responseInfo.reportStatus;
      this.reportAddendumStatus = responseInfo.addendumStatus;
      responseInfo.isDiscardXML = responseInfo.actionType === appConstants.reportActionTypes.CancelReport;
      // Update report closed status in Context Handler.
      log.debug(
        'reportSavedOrAutoSavedHandler -responseInfo.accessionInSaveEvent: ' + responseInfo.accessionInSaveEvent
      );
      if (responseInfo.accessionInSaveEvent === undefined || responseInfo.accessionInSaveEvent === '') {
        // Accession number is invalid. No need to continue.
        log.error('reportSavedOrAutoSavedHandler - Accession number is invalid in Save response');
        return;
      }

      const accessionNumbers = responseInfo.accessionInSaveEvent.split(',');
      const primaryIndex = accessionNumbers.indexOf(this.reportContextService.getReportContext());
      if (primaryIndex > -1) {
        const primary = accessionNumbers[primaryIndex];
        // Clear report context on save report
        this.reportContextService.setReportContext('');
        // Publish Report Closed event to third party applications
        const tab = this.utilService.getTabByAccession(primary);
        let reportStatus = UtilService.getExamStatusToUpdate(responseInfo.addendumStatus, responseInfo.reportStatus);
        if (responseInfo.reportStatus === appConstants.ReportStatus.Discarded) {
          reportStatus =
            this.reportAddendumStatus === appConstants.AddendumStatus.Yes ? REPORT_STATUS.FINAL : REPORT_STATUS.UNKNOWN;
        }
        const report = {
          accession: responseInfo.accessionInSaveEvent,
          status: reportStatus
        };
        const tabInterrupted = tab ? (tab.active ? false : true) : false;
        this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, tabInterrupted, appConstants.SUCCESS);
        // if report Id is empty and createReport is in progress, wait until it finishes.
        if (!this.reportContextService.getReportId() && this.reportSupportService.getCreateReportPromise()) {
          await this.reportSupportService.getCreateReportPromise();
        }
        // Setting examId and report id in response info for future use
        const nprContext = this.reportContextService.getNPRContext();
        responseInfo.reportId = nprContext.reportId;
        responseInfo.primaryId = nprContext.examId;
        const workingItem = {
          reportId: nprContext.reportId,
          auditData: this.reportContextService.getNPRContextAuditData()
        };
        this.reportSupportService.workingItem = workingItem;
        // Delete report from database while discarding the report on PowerScribe
        if (responseInfo.reportStatus === appConstants.ReportStatus.Discarded) {
          this.reportSupportService.handleReportDiscard(responseInfo.addendumStatus);
        }
        this.reportContextService.clearNPRContext();

        // Finding the primary accession number and corresponding context from the set of
        // accession numbers in case of associated reports. The accession numbers will be
        // separated by comma in the save event.
        const index = responseInfo.accessionInSaveEvent.indexOf(this.commaDelimiter);
        if (index !== -1) {
          log.info('reportSavedOrAutoSavedHandler : associated exam ');
          this.associatedReportSavedOrClosed(responseInfo);
        } else {
          log.info('reportSavedOrAutoSavedHandler : Single report ');
          // Get current context and then clear current context
          let currentContext = this.reportContextService.getContextCorrespondingToAccessionNum(primary);
          if (currentContext === '') {
            currentContext = responseInfo.primaryId;
          }
          // Sync exams in database when report is saved.
          if (responseInfo.reportStatus > 0) {
            this.reportSupportService.syncSavedExamsInDB(currentContext, primary);
          }
          this.reportSavedEventHandler(currentContext);
          if (responseInfo.actionType === appConstants.reportActionTypes.SaveOnCloseReport) {
            log.info('reportSavedOrAutoSavedHandler : Save report on tab close workflow ');
            if (UtilService.isNonAddendumReport(responseInfo)) {
              this.performActionInternal(
                'suspend',
                this.getWorkItemForAssociatedExams(currentContext, responseInfo.reportId)
              );
            }
          } else {
            this.publishEvents(responseInfo, currentContext);
          }
        }
      }
    }
  }

  /**
   * PowerScribe close event handler
   *  1. Display error message and disable the integration
   *  2. Save and close viewer in context
   */
  powerScribeCloseResponseHandler() {
    log.info('powerScribeCloseResponseHandler, shows re-launch confirmation dialog');
    // If logout is initiated no need of viewer closing and cache clearing since it was already performed from
    // cleanup method.
    if (!this.diService.isLogoutInitiated()) {
      if (
        this.dictationStatus !== appConstants.dictationIntegrationStatus.Disabled &&
        this.dictationStatus !== appConstants.dictationIntegrationStatus.None
      ) {
        // To keeps the dictation disabled temporarily, to prevent any xml being send from WFMP.
        // Enables dictation based on user input
        this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
        this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
        this.reportSupportService.showReportingCloseConfirmationDialog().then(data => {
          if (data === appConstants.YES) {
            this.sendReportingRelogin();
          } else {
            this.showReportingDisabledNotification();
          }
        });
      }
    }
  }

  /**
   * Different user logged in response handler
   */
  differentUserLoginResponseHandler() {
    log.info('differentUserLoginResponseHandler, shows re-login confirmation dialog');
    if (!this.diService.isLogoutInitiated()) {
      // Sending logout request to prevent opening further reports from PowerScribe
      // Login XML with empty user name will do the logout operation
      const isUserNameRequired = false;
      const LogoutXML = this.xmlTemplateManagerService.getPSLaunchXml(
        appConstants.windowState.Minimize,
        isUserNameRequired,
        appConstants.reportActionTypes.Login,
        this.reportingAppProcessName
      );
      this.reportSupportService.invokeAPIWrapper(LogoutXML, '');
      // To keeps the dictation disabled temporarily, to prevent any xml being send from WFM.
      // Enables dictation based on user input
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
      // Closes the dictation disabled dialog box if any
      if (this.disabledModalInstance) {
        this.disabledModalInstance.close();
      }
      if (!this.differentLoginModalInstance) {
        this.differentLoginModalInstance = this.modalService.openModal(
          this.translate.instant('di.differentUserLogged.critical.message'),
          {
            headerContent: this.translate.instant('di.desktopIntegration.title'),
            yesButtonLabel: this.translate.instant('label.relogin'),
            noButtonLabel: this.translate.instant('label.turnoff'),
            isDecisionModal: true,
            enableYesBtn: true,
            modalClass: 'modal--medium'
          }
        );
        this.modalCloseSubscription = this.differentLoginModalInstance.afterClosed.subscribe(data => {
          this.modalCloseSubscription.unsubscribe();
          if (data === appConstants.YES) {
            this.sendReportingRelogin();
          } else {
            this.showReportingDisabledNotification();
          }
          this.differentLoginModalInstance = null;
        });
      }
    }
  }

  /**
   * Shows dictation disabled critical notification and disable dictation on keep dictation off button click
   */
  showReportingDisabledNotification() {
    log.error('ShowReportingDisabledNotification, disabling dictation');
    this.disableDictationIntegration();

    if (this.reportContextService.getCurrentRequestedProcId() !== '') {
      // Save and close viewer corresponding to the context
      this.closeViewerCallback('close', this.reportContextService.getCurrentRequestedProcId());
    }
  }

  disableDictationIntegration(message?) {
    this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
    let modelMessage = message
      ? message
      : this.translate.instant('di.desktopIntegration.critical.dictationCannotLaunch');
    let modelHeaderContet;
    if (
      this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] &&
      this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] === 'false' &&
      this.utilService.getSystemProp()['SSO_Flag'] &&
      this.utilService.getSystemProp()['SSO_Flag'] === 'true'
    ) {
      modelHeaderContet = this.translate.instant('di.desktopIntegration.titleDictateDisabledSSO');
    } else if (this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] == 'true') {
      modelHeaderContet = this.translate.instant('di.desktopIntegration.titleDictateDisabledSSO');
      modelMessage = this.translate.instant('di.desktopIntegration.critical.dictationCannotLogin');
    } else {
      modelHeaderContet = this.translate.instant('di.desktopIntegration.titleDictateDisabled');
    }
    this.disabledModalInstance = this.modalService.openModal(modelMessage, {
      headerContent: modelHeaderContet
    });
    const modalSubscription = this.disabledModalInstance.afterClosed.subscribe(() => {
      modalSubscription.unsubscribe();
      this.disabledModalInstance = null;
    });
    this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    // Clear report queue on disabling the dictation integration
    this.reportSupportService.clearMessageQueue();
    // Release read lock for the active exam
    this.reportSupportService.releaseReadLock();
  }

  /**
   * Launch powerscribe on re-launch option click
   */
  sendReportingRelogin() {
    if (this.reportSupportService.getApplicationTerminateStatus()) {
      this.reportSupportService.setApplicationTerminateStatus(false);
      this.integrationStatusService.setReportingPluginStatus(false);
      this.messageService.broadcast('loadPlugin');
    } else {
      log.info('SendReportingRelogin, sending launch xml');
      if (!this.diService.isLogoutInitiated()) {
        const isUserNameRequired = true;
        const state = this.reportSupportService.getAutoMinimizePreference()
          ? appConstants.windowState.Minimize
          : appConstants.windowState.NoStateChange;
        const reportingLaunchXML = this.xmlTemplateManagerService.getPSLaunchXml(
          state,
          isUserNameRequired,
          appConstants.reportActionTypes.Login,
          this.reportingAppProcessName
        );
        this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
        this.reportSupportService.invokeAPIWrapper(reportingLaunchXML, undefined);
        this.dictationStatus = appConstants.dictationIntegrationStatus.LaunchApplicationInitiated;
        this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.ForceMinimize, undefined);
      }
    }
  }

  performActionInternal(actionName, workItem) {
    let tab = this.utilService.getTab(workItem.id);
    if (appConstants.externalTabId !== workItem.id) {
      log.debug('Starting performActionInternal ' + JSON.stringify(workItem) + ' actionName ' + actionName);
      workItem.keys.source = 'desktop-integration';
      workItem.reportStatus = UtilService.getExamStatusToUpdate(this.reportAddendumStatus, this.reportStatusFromPS);
      if (tab && tab.workItem && tab.workItem.keys) {
        workItem.keys.timeOfExam = tab.workItem.keys.timeOfExam;
        workItem.keys.procedureStatusId = tab.workItem.keys.procedureStatusId;
      }

      this.eventService.sendEvent(new EventMessage(Events[actionName], workItem)).then(
        () => {
          log.info(
            'performActionInternal remote action ' + actionName + ' succeeded for Request ProcedureID ' + workItem.id
          );
          if (workItem.reportStatus > 0) {
            this.reportSupportService.syncSavedExamsReportStatus(workItem);
          }
        },
        error => {
          log.error(
            'performActionInternal remote action ' +
              actionName +
              ' for failed with error: ' +
              error +
              ' for Requested ProcedureID: ' +
              workItem.id
          );
        }
      );
      log.info('Exiting performActionInternal');
    } else {
      this.reportContextService.clearDxHubContext(appConstants.externalTabId);
      this.reportContextService.removeContext(appConstants.externalTabId);
      tab = this.utilService.getTab(workItem.id);
      if (tab && tab.isExternalTab) {
        tab.externalTabMsgFailed = false;
        this.messageService.broadcast('removeExternalTab', tab.id);
      }
    }
  }

  performAction(actionName, workItem) {
    // Clear performance hook
    PerformanceMonitorService.clear(PerformanceSource.REPORT);
    log.info('performAction - action - ' + actionName + ' for ' + workItem.id);
    // Check whether viewer context is present and it is closable
    if (workItem.id !== '' && this.checkViewerContextCallback(workItem.id)) {
      this.callBackOnViewerClose = () => {
        log.info(
          'performAction -call performActionInternal on viewer close callback - action - ' +
            actionName +
            ' for ' +
            workItem.id
        );

        this.performActionInternal(actionName, workItem);
      };

      // Invoke close viewer callback to close the viewer with either save or discard option
      // If close viewer request failed cancel callback and send state handling request
      if (!this.closeViewerCallback(actionName, workItem.id)) {
        log.info('performAction -close viewer failed skip - action - ' + actionName);
        this.callBackOnViewerClose = undefined;
      }
    } else {
      log.info('performAction - call performActionInternal directly - action - ' + actionName + ' for ' + workItem.id);
      this.performActionInternal(actionName, workItem);
    }
  }

  getModuleName() {
    return this.REPORTING_CONFIG.MODULENAME;
  }

  getDictationStatus() {
    return this.dictationStatus;
  }

  setDictationStatus(status) {
    this.dictationStatus = status;
  }

  /**
   * Method to subscribe required responses and events, load plugin
   */
  subscribeResponseAndEvents(loadInvokeAPICallback) {
    // Setting callback for setcontext timeout
    this.reportSupportService.setSetContextTimeoutCallback(this.handleSetContextTimeout);

    // Subscribe the load response from PSPlugin
    const loadResponse = this.xmlTemplateManagerService.getSubscribeXml(
      'LS_PLUGIN_LOAD_RESPONSE',
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the load response from PSPlugin');
    loadInvokeAPICallback(loadResponse);

    // Subscribe response from LineShaftPluginManager. If PSPlugin is missing DI receives
    // response only from LineShaftPluginManager.
    const loadResponseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
      this.REPORTING_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_LOAD_RESPONSE',
      'LineShaftPluginManager'
    );
    log.info('Subscribe the load response from LineShaftPluginManager');
    loadInvokeAPICallback(loadResponseFromPluginManager);

    // Subscribe the USER_LOGIN response from PSPlugin
    const psReportLoginResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.USER_LOGIN_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the USER_LOGIN response from PSPlugin');
    loadInvokeAPICallback(psReportLoginResponse);

    // Subscribe the setcontext response from PSPlugin
    const psSetcontextResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.REPORTING_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_SETCONTEXT_RESPONSE',
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the setcontext response from PSPlugin');
    loadInvokeAPICallback(psSetcontextResponse);

    // Subscribe the OPEN_REPORT from PSPlugin
    const psOpenReport = this.xmlTemplateManagerService.getSubscribeXml(
      this.OPEN_REPORT_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the OPEN_REPORT from PSPlugin');
    loadInvokeAPICallback(psOpenReport);

    // Subscribe the REPORT_SAVED response from PSPlugin
    const psReportSavedResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.REPORT_SAVED_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the REPORT_SAVED response from PSPlugin');
    loadInvokeAPICallback(psReportSavedResponse);

    // Subscribe the REPORT_OPENED response from PSPlugin
    const psReportOpenedResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.REPORT_OPENNED_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the REPORT_OPENED response from PSPlugin');
    loadInvokeAPICallback(psReportOpenedResponse);

    // Subscribe the REPORT_AUTO_SAVED response from PSPlugin
    const psReportAutoSavedResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.REPORT_AUTO_SAVED_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the REPORT_AUTO_SAVED response from PSPlugin');
    loadInvokeAPICallback(psReportAutoSavedResponse);

    if (this.REPORTING_CONFIG.ASSOCIATION_ENABLED) {
      // Subscribe the REPORT_ASSOCIATED response from PSPlugin
      const psReportAssociationResponse = this.xmlTemplateManagerService.getSubscribeXml(
        this.REPORT_ASSOCIATED_TOPIC,
        this.REPORTING_CONFIG.MODULENAME
      );
      log.info('Subscribe the REPORT_ASSOCIATED response from PSPlugin');
      loadInvokeAPICallback(psReportAssociationResponse);
    }

    this.subscribeResponseAndEventsForModalDialogs(loadInvokeAPICallback);

    // Subscribe the PowerScribe_CLOSE response from PSPlugin
    const psCloseResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.POWERSCRIBE_CLOSE_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the PowerScribe_CLOSE response from PSPlugin');
    loadInvokeAPICallback(psCloseResponse);
  }

  subscribeResponseAndEventsForModalDialogs(loadInvokeAPICallback) {
    // Subscribe the PS_CHILD_WINDOW response from PSPlugin
    const psChildWindowResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.PS_CHILD_WINDOW_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the PS_CHILD_WINDOW response from PSPlugin');
    loadInvokeAPICallback(psChildWindowResponse);

    // Subscribe the PS_MODAL_DIALOG_TOPIC response from PSPlugin
    const psModalDialogResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.PS_MODAL_DIALOG_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the PS_MODAL_DIALOG_TOPIC response from PSPlugin');
    loadInvokeAPICallback(psModalDialogResponse);

    // Subscribe the USER_LOGIN event response from PSPlugin
    const psReportUserLoginResponse = this.xmlTemplateManagerService.getSubscribeXml(
      this.USER_LOGGEDIN_TOPIC,
      this.REPORTING_CONFIG.MODULENAME
    );
    log.info('Subscribe the USER_LOGIN event from PSPlugin');
    loadInvokeAPICallback(psReportUserLoginResponse);
  }

  /**
   * Method to publish event for non addendum report save/discard action in PowerScribe
   */
  publishEventForNonAddendum(responseInfo, primaryId, associationList) {
    if (responseInfo.topicName === this.REPORT_SAVED_TOPIC) {
      this.performAction(
        'close',
        this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
      );
    } else {
      // If report is closed without saving(by clicking on close button), no need to publish
      // READ_CLOSE_SAVE_FOR_LATER event.
      if (!responseInfo.isDiscardXML) {
        log.info('publishEventForNonAddendum - publishEventForNonAddendum - call suspend directly');
        // If the topic is not REPORT_SAVED then it will be suspend so execute READ_CLOSE_SAVE_FOR_LATER
        // event.
        this.performActionInternal(
          'suspend',
          this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
        );
      }
    }
  }

  /**
   * Method to publish event for non final addendum report save/discard action in PowerScribe
   */
  publishEventForNonFinalAddendum(responseInfo, primaryId, associationList) {
    log.info(
      'publishEventForNonFinalAddendum - publishEventForNonFinalAddendum - ' + 'call clear for contextId' + primaryId
    );
    if (primaryId !== undefined) {
      if (responseInfo.topicName !== this.REPORT_AUTO_SAVED_TOPIC) {
        // No need to invoke clear action for addendum report on interrupted scenario(ReportSavedForLater)
        this.performAction(
          'clear',
          this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
        );
      }
    }
  }

  /**
   * To force minimize the PowerScribe on contextId empty. No need to clear current context here as it is
   * already done.
   */
  forceMinimizePowerScribe() {
    if (!this.reportSupportService.isOpenReportRequestPresentInQueue()) {
      log.info('isReportClosedEvent - Context is empty. Sent hide request to PSPlugin');
      const tab = this.utilService.getActiveTab();
      if (tab && !tab.isExternalTab) {
        this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.ForceMinimize, undefined);
      }
    }
  }

  /**
   * Method to publish events on saving report in PowerScribe
   */
  publishEvents(responseInfo, primaryId, associationList?) {
    try {
      if (associationList && associationList.length > 0) {
        this.examCacheService.setAssociateExamCache(primaryId);
        _.each(associationList, id => {
          this.examCacheService.setAssociateExamCache(id);
        });
      }
      // Getting requested procedure ID from Context-handler by passing accession number and
      // send that in publish event
      log.debug('Starting publishEvents responseInfo ' + responseInfo.toString() + ' contextId ' + primaryId);

      if (primaryId === '' && !this.diService.isLogoutInitiated()) {
        log.info('publishEvents - Context is empty.');
        this.forceMinimizePowerScribe();
        return;
      }

      // Publish readAndCloseExam event if status is
      // WetRead/Draft/PendingCorrection/Corrected/PendingSignature/Final
      // else publish READ_CLOSE_DISCARD event
      if (UtilService.isNonAddendumReport(responseInfo)) {
        this.publishEventForNonAddendum(responseInfo, primaryId, associationList);
      } else if (UtilService.isNonFinalAddendumReport(responseInfo)) {
        this.publishEventForNonFinalAddendum(responseInfo, primaryId, associationList);
      } else if (
        appConstants.AddendumStatus.Yes === responseInfo.addendumStatus &&
        appConstants.ReportStatus.Final === responseInfo.reportStatus &&
        !responseInfo.isDiscardXML
      ) {
        this.performAction(
          'close',
          this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
        );
      } else {
        log.info('publishEvents: report is discarded');
        if (responseInfo.topicName === this.REPORT_SAVED_TOPIC) {
          // Send closediscard for other report status like Discarded/CorrectionRejected/SignRejected
          this.performAction(
            'closediscard',
            this.getWorkItemForAssociatedExams(primaryId, responseInfo.reportId, associationList)
          );
        }
      }
    } catch (error) {
      log.error('publish event failed: ' + error.description + ' for requested procedure ID : ' + primaryId);
    }
    log.debug('Exiting publishEvents');
  }

  getWorkItemForAssociatedExams(primary, reportId, associationList?): EventWorkItem {
    const workItem = new EventWorkItem(primary);
    workItem.associationList = associationList;
    workItem.reportId = reportId;
    return workItem;
  }

  /**
   * Method to get the state for setContext request.
   * Supposed to invoke from setContext only.
   */
  getStateForSetContext(state) {
    if (state !== 'SHOW_ON_TOP') {
      // If context is present use the state value otherwise the state node value will be MINIMIZE.
      const applicationContext = this.diService.getApplicationContext();
      state = _.isNull(applicationContext)
        ? appConstants.windowState.Minimize
        : applicationContext === appConstants.externalTabId && !this.isSaveActionFailed
        ? appConstants.windowState.ShowOnTop
        : state;
    }
    return state;
  }

  /**
   * This method is responsible for open report, save  report and/or update PowerScribe sate(  minimize, maximize,
   * force minimize).
   * Set context before calling this function.
   */
  setContext(state, actionType, accessionFromSignQueue, siteNameFromSignQueue?) {
    const action =
      actionType === 'FORCEMINIMIZE'
        ? _.isNull(this.diService.getApplicationContext())
          ? 'MINIMIZE'
          : actionType
        : actionType;
    state = this.getStateForSetContext(state);
    log.info(
      'setContext accession : ' +
        this.reportContextService.getCurrentAccession() +
        ', state : ' +
        state +
        ', actionType : ' +
        action
    );
    const accession = accessionFromSignQueue || this.reportContextService.getCurrentAccession();
    const siteName =
      siteNameFromSignQueue ||
      (this.reportContextService.getSiteName() === undefined ? '' : this.reportContextService.getSiteName());
    let associableExams = '';
    if (actionType === appConstants.reportActionTypes.OpenReport) {
      associableExams = this.tabContextService.getAssociationListAccNumbers() || '';
    }
    // If Auto minimize preference is off, update the state to NoStateChange
    if (!this.reportSupportService.getAutoMinimizePreference()) {
      if (state === appConstants.windowState.Minimize) {
        state = appConstants.windowState.NoStateChange;
      }
    }
    let psPluginContext = this.xmlTemplateManagerService.getPSSetContextXml(
      accession,
      associableExams,
      state,
      siteName,
      this.reportContextService.getDiscardFlag(),
      action
    );
    if (actionType === 'TERMINATE') {
      log.info('setContext  actionType: ' + actionType);
      psPluginContext = this.xmlTemplateManagerService.getTerminateXml(
        this.REPORTING_CONFIG.OWNERINSTANCE,
        this.reportingAppProcessName
      );
    }
    if (this.diService.isLogoutInitiated() || actionType === 'TERMINATE') {
      log.info('setContext - logout is initiated send direct xml to plugin ' + 'to avoid request queuing');
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
      this.diService.invokeAPIWrapperExecute(psPluginContext);
    } else {
      // Skip setting the reporting status to pendingResponse if the discard flag is set
      // Skip setting the reporting status to pendingResponse if the action type is MINIMIZE or FORCEMINIMIZE
      if (actionType !== 'FORCEMINIMIZE' && actionType !== 'MINIMIZE' && !this.reportContextService.getDiscardFlag()) {
        this.integrationStatusService.setReportingHealthStatus(
          this.integrationStatusService.healthStatusEnum.pendingResponse
        );
      }
      this.reportSupportService.invokeAPIWrapper(psPluginContext, accession, actionType);
    }
  }

  /**
   * reportOpenEventHandler - Handler for REPORT_OPENNED event from PSPlugin
   * PSPlugin trigger this event only for manual report launch
   */
  reportOpenEventHandler(response) {
    if (this.dictationStatus !== appConstants.dictationIntegrationStatus.Disabled) {
      this.accessionFromSignQueue = response.accessionInSaveEvent;

      if (!_.isUndefined(this.accessionFromSignQueue) && !_.isEmpty(this.accessionFromSignQueue)) {
        const accessions = this.accessionFromSignQueue.split(',');
        const primary = accessions[0];
        accessions.splice(0, 1);
        this.reportContextService.setReportContext(primary);
        const activeTabId = this.utilService.getActiveTab()?.id;
        this.reportSupportService.setBidirectionalContext(primary).then(
          workItemId => {
            // Publish Report Opened event to third party applications
            const report = {
              accession: response.accessionInSaveEvent,
              status: UtilService.getExamStatusToUpdate(response.addendumStatus, response.reportStatus)
            };
            this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.SUCCESS);
            if (this.REPORTING_CONFIG.ASSOCIATION_ENABLED && accessions.length > 0) {
              if (activeTabId === workItemId) {
                this.publishReportAssociation(workItemId, response.accessionInSaveEvent);
              } else {
                this.tabContextService.saveState(
                  workItemId,
                  'associableExamsOnReportOpen',
                  response.accessionInSaveEvent
                );
              }
            }
          },
          error => {
            if (error === appConstants.FOUND_NONE) {
              this.showExamNotFoundModal();
            } else if (error === appConstants.MAX_TAB_ERROR) {
              this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
            }
            log.error(
              'reportOpenEventHandler - set context from PS failed for accession: ' +
                this.accessionFromSignQueue +
                ', ERROR: ' +
                error
            );
          }
        );
      }
    }
  }

  showExamNotFoundModal(): void {
    this.notify.critical(
      this.translate.instant('collaboration.openlink.modal.recordNotAccessibleMessage'),
      this.translate.instant('collaboration.openlink.modal.recordNotAccessibleTitle')
    );
  }

  /**
   * reportAssociationEventHandler - Handler for REPORT_ASSOCIATED event from PSPlugin
   * PSPlugin trigger this event when association/dissociation happens in PowerScribe
   */
  reportAssociationEventHandler(response) {
    if (
      this.dictationStatus !== appConstants.dictationIntegrationStatus.Disabled &&
      !_.isEmpty(response.accessionInSaveEvent)
    ) {
      this.publishReportAssociation(
        this.reportContextService.getCurrentRequestedProcId(),
        response.accessionInSaveEvent
      );
    }
  }

  /**
   * Method to handle open report success response
   */
  handleOpenReportSuccessResponse(response) {
    if (response.accession !== '' && !response.isDiscardXML) {
      log.info('handleOpenReportSuccessResponse - accession: ' + response.accession);

      this.messageService.broadcast('isReportOpened', { accession: response.accession, flag: true });
      PerformanceMonitorService.stop(PerformanceSource.REPORT, response.accession);
      // Publish Report Opened event to third party applications
      const report = {
        accession: response.associatedAccession || response.accession,
        status: UtilService.getExamStatusToUpdate(response.addendumStatus, response.reportStatus)
      };
      this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, response.resultValue);
      // Create report meta data in data base if it is not present.
      const examContext = this.currentExamContext.getFullContext();
      if (examContext && examContext.keys.procedureAccessionNo === response.accession) {
        const reportId = examContext.keys.reportId ? examContext.keys.reportId : null;
        if (reportId === null) {
          this.reportSupportService.handleCreateReport(examContext.id, response.accession);
        } else {
          this.reportContextService.setNPRContext(reportId, examContext.id, response.accession);
        }
        if (!UtilService.isActivatedBySelf(examContext, this.appConfigService.getUserId())) {
          log.debug('handleOpenReportSuccessResponse - Get Read Lock : ', examContext.keys.procedureAccessionNo);
          this.eventService.sendEvent(new EventMessage(Events.read, examContext));
        }
      }
      // Reset cancel report accession on launching new report
      this.cancelReportAccession = '';
      // Set report context on open report
      this.reportContextService.setReportContext(response.accession);
      this.setLaunchDictationFlag(true);
    }
    const currentContext = this.reportContextService.getCurrentRequestedProcId();
    const applicationContext = this.diService.getApplicationContext();
    log.debug(
      'handleOpenReportSuccessResponse - Open Report Success - getCurrentContext : ' +
        currentContext +
        ', Accession Number : ' +
        response.accession +
        ', Application context : ' +
        applicationContext +
        ', Discard Flag : ' +
        response.isDiscardXML
    );
    // Reset the save action failed flag on getting the open report success response.
    if (
      !response.isDiscardXML &&
      response.accession !== '' &&
      response.state === appConstants.windowState.ShowOnTop &&
      this.isSaveActionFailed
    ) {
      this.isSaveActionFailed = false;
      this.saveFailedAccession = '';
    }
    // Reset the cancel action failed flag on getting the open report success response.
    if (
      !response.isDiscardXML &&
      response.accession !== '' &&
      response.state === appConstants.windowState.ShowOnTop &&
      this.isCancelActionFailed
    ) {
      this.isCancelActionFailed = false;
      this.cancelFailedAccession = '';
    }

    if (this.REPORTING_CONFIG.ASSOCIATION_ENABLED) {
      this.tabContextService.saveState(currentContext, 'associableExamsOnReportOpen', response.associatedAccession);
      this.publishReportAssociation(currentContext, response.associatedAccession);
    }
  }

  setLaunchDictationFlag(flag) {
    const tab = this.utilService.getActiveTab();
    if (tab && tab.workItem && tab.workItem.keys) {
      tab.workItem.keys.launchDictation = flag;
    }
  }

  /**
   * Method to check is save or cancel success response
   */
  isSaveCancelSuccessResponse(response) {
    if (
      (response.actionType === appConstants.reportActionTypes.CancelReport ||
        response.actionType === appConstants.reportActionTypes.SaveOnCloseReport) &&
      !this.diService.isLogoutInitiated()
    ) {
      // Reset cancel report accession on receiving cancel success response
      this.cancelReportAccession = '';
    }
    return (
      response.actionType === appConstants.reportActionTypes.SaveReport ||
      response.actionType === appConstants.reportActionTypes.SaveOnCloseReport ||
      response.actionType === appConstants.reportActionTypes.CancelReport
    );
  }

  /**
   * Method to check is minimize or force minimize with modal dialog success response
   */
  isMinimizeForceMinimizeWithModalDlgSuccessResponse(response) {
    return (
      response.accession !== '' &&
      (response.state === appConstants.windowState.Hide || response.state === appConstants.windowState.Minimize)
    );
  }

  /**
   * Method to check is minimize or force minimize  success response
   */
  isMinimizeForceMinimizeSuccessResponse(response) {
    return (
      response.actionType === appConstants.reportActionTypes.ForceMinimize ||
      response.actionType === appConstants.reportActionTypes.Minimize
    );
  }

  /**
   * Method to handle success setcontext response
   */
  handleSuccessSetcontextResponse(response) {
    log.debug(
      'handleSuccessSetcontextResponse - userName : ' +
        response.userName +
        ' , accession : ' +
        response.accession +
        ', state : ' +
        response.state +
        ', actionType : ' +
        response.actionType +
        ' isDiscardXML : ' +
        response.isDiscardXML
    );
    if (response.actionType === appConstants.reportActionTypes.Logout) {
      log.info('handleSuccessSetcontextResponse - PowerScribe logout and force Minimize Success');
    } else if (this.isMinimizeForceMinimizeWithModalDlgSuccessResponse(response)) {
      log.info('handleSuccessSetcontextResponse - Report minimize / force minimize success' + ' with open dialog box');
    } else if (
      response.actionType === appConstants.reportActionTypes.Login &&
      response.actionStatus !== 'ACTION_INPROGRESS'
    ) {
      if (response.userName && response.userName.toLowerCase() === this.appConfigService.getUserId().toLowerCase()) {
        log.info('handleSuccessSetcontextResponse - PowerScribe Startup and login success');
        this.enableIntegrationAndSetContext();
      }
    } else if (this.isSaveCancelSuccessResponse(response)) {
      // To Do:: Do we need this after split save?
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
      log.info('handleSuccessSetcontextResponse - Save and Close Report Success');
    } else if (this.isMinimizeForceMinimizeSuccessResponse(response)) {
      log.info('handleSuccessSetcontextResponse - PowerScribe ' + response.actionType + ' success');
    } else if (response.actionType === appConstants.reportActionTypes.OpenReport) {
      this.openReportInProgress(response);
    } else {
      log.info('handleSuccessSetcontextResponse - setcontext response success');
      this.isPreviewReportSuccessResponse(response);
    }
  }

  enableIntegrationAndSetContext() {
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.Disabled) {
      this.notify.success(
        this.translate.instant('di.desktopIntegration.success.loadDictationSuccessAfterTimeout'),
        this.translate.instant('di.desktopIntegration.titleDictationEnabled')
      );
    }
    this.dictationStatus = appConstants.dictationIntegrationStatus.Enabled;
    this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
    // Closes the dictation disabled dialog box if any on successful login
    if (this.disabledModalInstance) {
      this.disabledModalInstance.close();
    }
    // Closes the different user login dialog box if any on successful login
    if (this.differentLoginModalInstance) {
      this.modalCloseSubscription.unsubscribe();
      this.differentLoginModalInstance.close();
      this.differentLoginModalInstance = null;
    }
    const tab = this.utilService.getActiveTab();
    if (tab) {
      this.eventService.sendEvent(
        new EventMessage(Events[tab.examContext], {
          workItem: tab.workItem
        })
      );
    } else {
      this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
    }
  }

  openReportInProgress(response) {
    if (response.actionStatus !== 'ACTION_INPROGRESS' && !response.isDiscardXML) {
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
      this.handleOpenReportSuccessResponse(response);
    } else {
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.pendingResponse
      );
    }
  }

  /**
   * Method to handle Preview Report Success
   */
  isPreviewReportSuccessResponse(response) {
    if (response.actionType === appConstants.reportActionTypes.PreviewReport) {
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
      log.info('handleSuccessSetcontextResponse - Preview Report Success');
    }
  }

  handlePSStartupFailure(response) {
    log.error('handlePSStartupFailure - PowerScribe Startup Failed');
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.LaunchApplicationInitiated) {
      if (response.actionStatus !== 'USERINPUT_PENDING' && response.actionStatus !== 'ACTIONNOT_COMPLETED') {
        // During login if PowerScribe already has a report opened, it will try to save and close the report.
        // Due to some reasons(popup or API failure) if plugin failed to close the report, possible out of context
        // scenario. To prevent this we will notify the user and automatically signout on dismissing the dialog box.
        if (
          response.dictationFailedAction === 'SAVE' ||
          response.dictationFailedAction === 'CANCEL' ||
          response.dictationFailedAction === 'LOGOUT'
        ) {
          log.error(
            'handlePSStartupFailure - Previous report failed to close, signout automatically to ' +
              'prevent out of context.'
          );
          this.notify
            .critical(
              this.translate.instant('desktopIntegration.dictation.contextSwitchFailure'),
              this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
            )
            .then(() => {
              this.signOutService.signOut();
            });
          return;
        }
        if (
          this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] &&
          this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] === 'false' &&
          this.utilService.getSystemProp()['SSO_Flag'] &&
          this.utilService.getSystemProp()['SSO_Flag'] === 'true'
        ) {
          this.disableDictationIntegration(
            this.translate.instant('di.desktopIntegration.critical.dictationCannotLogin')
          );
        } else {
          const message = response.loginResponse
            ? this.translate.instant('di.desktopIntegration.critical.dictationCannotLogin')
            : undefined;
          this.disableDictationIntegration(message);
        }
        // Sending launch xml with empty name to minimize the PowerScribe if login fails
        if (response.userName !== '') {
          const isUserNameRequired = false;
          log.error('handlePSStartupFailure - PowerScribe login failed');
          const reportingLaunchXMLWithEmptyUserName = this.xmlTemplateManagerService.getPSLaunchXml(
            appConstants.windowState.Minimize,
            isUserNameRequired,
            appConstants.reportActionTypes.Logout,
            this.reportingAppProcessName
          );
          this.reportSupportService.invokeAPIWrapper(reportingLaunchXMLWithEmptyUserName, '');
        }
      }
    }
  }

  /**
   * Method to handle save/cancel failure because of ACTION_TIMEOUT or NON_RESPONSIVE
   */
  handleSaveCancelDictationFailure(response) {
    // Save/Cancel =>If failure type  = ACTION_TIMEOUT or NON_RESPONSIVE then dictation must be disabled
    //            Example scenario - PowerScribe server is down
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.Enabled) {
      // SetContext response with SAVE/CANCEL action failure happens only in interrupt scenario.
      // If failure type is ACTION_TIMEOUT/NON_RESPONSIVE, that means dictation is not possible for
      // this user session. That is why user notified with DictationCannotLaunch message instead of
      // SaveFailed or CancelFailed message
      log.error(
        'handleSaveCancelDictationFailure - report save/cancel failed and dictation' +
          ' disabled, action: ' +
          response.dictationFailedAction +
          ', failure type: ' +
          response.failureType +
          ', failure reason: ' +
          response.resultMsg
      );
      this.disableDictationIntegration();
    }
  }

  /**
   * Method to handle save action failure
   */
  handleSaveActionFailure(responseInfo, isNotify) {
    // Clear message queue on save failure
    this.reportSupportService.clearMessageQueue();

    // Failure type = ACTION_FAILED means failed to save current exam
    //            Example scenario - Modal dialog in PowerScribe, user action required
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.Enabled) {
      if (!this.isSaveActionFailed) {
        this.isSaveActionFailed = true;
        if (responseInfo.actionType === appConstants.reportActionTypes.SaveReport) {
          this.saveFailedAccession = this.isCancelActionFailed
            ? this.cancelFailedAccession
            : this.reportContextService.getPreviousAccession();
        }
        if (responseInfo.actionType === appConstants.reportActionTypes.SaveOnCloseReport) {
          this.saveFailedAccession = this.cancelReportAccession;
        }
      }

      log.error(
        'handleSaveActionFailure - failed to save report, accession: ' +
          this.saveFailedAccession +
          ', failure type : ACTION_FAILED'
      );
      if (isNotify) {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.warning.saveReportFailed') + ' ' + this.saveFailedAccession,
          this.translate.instant('di.desktopIntegration.title')
        );
      }
      this.setContext(
        appConstants.windowState.Hide,
        appConstants.reportActionTypes.ForceMinimize,
        this.saveFailedAccession
      );
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed
      );
    }
  }

  handlePreviewReportFailure(response) {
    log.error('handlePreviewReportActionFailure - failed to preview report, accession: ' + response.accession);
    this.notify.warning(
      this.translate.instant('di.desktopIntegration.warning.previewReportFailed').replace(/ACC#/g, response.accession),
      this.translate.instant('di.desktopIntegration.title')
    );
    // Directly passing the failed accession number in the request to avoid previous context
    // setting and resetting.
    this.reportContextService.setCurrentContext(this.reportContextService.getCurrentRequestedProcId, '', undefined);
    this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.ForceMinimize, undefined);
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.lastActionFailed
    );
  }

  /**
   * Method to handle cancel action failure
   */
  handleCancelActionFailure(responseInfo, isNotify) {
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.Enabled) {
      if (!this.isCancelActionFailed) {
        this.isCancelActionFailed = true;
        this.cancelFailedAccession = this.isSaveActionFailed ? this.saveFailedAccession : this.cancelReportAccession;
      }

      log.error(
        'handleCancelActionFailure -Failed to cancel report, accession:  ' +
          this.cancelFailedAccession +
          'failure type: ACTION_FAILED'
      );
      if (isNotify) {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.warning.closeReportFailed') + ' ' + this.cancelFailedAccession,
          this.translate.instant('di.desktopIntegration.title')
        );
      }
      this.setContext(
        appConstants.windowState.Hide,
        appConstants.reportActionTypes.ForceMinimize,
        this.cancelFailedAccession
      );
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed
      );
    }
  }

  /**
   * Method to handle save/cancel failure
   */
  handleSaveCancelFailure(response, isNotify) {
    log.error('handleSaveCancelFailure');
    let tab = null;
    if (this.reportContextService.getReportContext() !== '') {
      const accession = this.reportContextService.getReportContext();
      tab = this.utilService.getTabByAccession(accession);
      const tabInterrupted = tab ? (tab.active ? false : true) : false;
      this.publishReportingEvent(REPORTING_EVENT.CLOSE, { accession: accession }, tabInterrupted, response.resultValue);
    }
    // Save/Cancel =>If failure type  = ACTION_TIMEOUT or NON_RESPONSIVE then dictation must be disabled
    //            Example scenario - PowerScribe server is down
    // Failure type = ACTION_FAILED means failed to save/cancel current exam
    //            Example scenario - Modal dialog in PowerScribe, user action required
    if (
      (response.dictationFailedAction === 'SAVE' || response.dictationFailedAction === 'CANCEL') &&
      (response.failureType === 'ACTION_TIMEOUT' || response.failureType === 'NON_RESPONSIVE')
    ) {
      this.handleSaveCancelDictationFailure(response);
    } else if (response.dictationFailedAction === 'SAVE' && response.failureType === 'ACTION_FAILED') {
      this.handleSaveActionFailure(response, isNotify);
    } else if (response.dictationFailedAction === 'CANCEL' && response.failureType === 'ACTION_FAILED') {
      this.handleCancelActionFailure(response, isNotify);
    } else {
      log.error('handleSaveCancelFailure - Save and Close Report Failed');
    }
  }

  /**
   * Method to handle open failure
   */
  handleOpenFailure(response) {
    this.messageService.broadcast('isReportOpened', { accession: response.accession, flag: false });
    if (response.actionType === appConstants.reportActionTypes.OpenReport) {
      // Publish Report Opened event with failure result to third party applications
      const report = { accession: response.accession };
      this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, response.resultValue);
    }
    this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
    if (
      response.dictationFailedAction === 'OPEN' &&
      (response.failureType === 'ACTION_TIMEOUT' || response.failureType === 'NON_RESPONSIVE')
    ) {
      if (this.dictationStatus === appConstants.dictationIntegrationStatus.Enabled) {
        log.error(
          'handleOpenFailure -Failed to launch report and dictation is' +
            ' disabled, accession: ' +
            response.accession +
            ', failure type: ' +
            response.failureType +
            ', failure reason: ' +
            response.resultMsg
        );
        this.disableDictationIntegration();
      }
    } else if (response.dictationFailedAction === 'OPEN' && response.failureType === 'ACTION_FAILED') {
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed
      );
      log.error(
        'handleOpenFailure - Open report failed, accession: ' +
          response.accession +
          ', failure type: ' +
          response.failureType
      );
      // Keep the open report failed modal object, to close it automatically when child window popup comes on top of it
      this.openReportModalInstance = this.modalService.openModal(
        this.translate.instant('di.desktopIntegration.warning.openReportFailed') + ' ' + response.accession,
        {
          headerContent: this.translate.instant('di.desktopIntegration.title')
        }
      );
      const modalSubscription = this.openReportModalInstance.afterClosed.subscribe(() => {
        modalSubscription.unsubscribe();
        this.openReportModalInstance = undefined;
      });
      this.resetTabOnReportFailure(response.accession);
    } else if (response.dictationFailedAction === 'OPEN' && response.failureType === 'DUPLICATE_SETCONTEXT') {
      log.info('handleOpenFailure - Discard the response of OpenReport cached xml');
    } else if (
      response.actionType === appConstants.reportActionTypes.OpenReport &&
      response.actionStatus === 'ACTIONNOT_COMPLETED'
    ) {
      log.error('handleOpenFailure - Open report skipped, accession: ' + response.accession);
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.openReportFailed'),
        this.translate.instant('di.desktopIntegration.title')
      );
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed
      );
    } else if (response.actionType === appConstants.reportActionTypes.OpenReport) {
      log.error('handleOpenFailure - Open Report Failed - Accession Number: ' + response.accession);
    }

    // Remove context from context list if it is failed to open.
    this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
  }

  /**
   * Method to handle failure Setcontext response
   */
  handleFailureSetcontextResponse(response) {
    log.debug(
      'handleFailureSetcontextResponse - userName : ' +
        response.userName +
        ' , accession : ' +
        response.accession +
        ', state : ' +
        response.state +
        ', actionType : ' +
        response.actionType
    );
    if (response.actionType === appConstants.reportActionTypes.Login) {
      this.handlePSStartupFailure(response);
    } else if (response.userName === '') {
      log.error('handleFailureSetcontextResponse - PowerScribe logout Failed');
    } else if (this.isSaveCancelFailureResponse(response.actionType)) {
      this.isNotify = true;
      this.handleSaveCancelFailure(response, this.isNotify);
    } else if (
      response.actionType !== appConstants.reportActionTypes.PreviewReport &&
      response.actionType !== appConstants.reportActionTypes.Minimize &&
      response.actionType !== appConstants.reportActionTypes.ForceMinimize &&
      (response.dictationFailedAction === 'SAVE' || response.dictationFailedAction === 'CANCEL')
    ) {
      this.isNotify = this.isNotify ? false : true;
      this.handleSaveCancelFailure(response, this.isNotify);
      this.isNotify = false;
    } else {
      this.handleOpenFailure(response);
      if (this.diService.getApplicationContext() === null) {
        // Seems like this scenario is not reproducible because reporting queue is present
        this.reportSupportService.hideAllOpenViewers();
      }
      this.integrationStatusService.setReportingHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed
      );
    }
    // Release read lock on Open Report failure
    if (response.actionType === appConstants.reportActionTypes.OpenReport) {
      const tab = this.utilService.getTabByAccession(response.accession);
      if (tab) {
        this.reportSupportService.releaseReadLock(tab.id);
      }
    }
  }

  isSaveCancelFailureResponse(actionType) {
    if (
      actionType === appConstants.reportActionTypes.SaveReport ||
      actionType === appConstants.reportActionTypes.SaveOnCloseReport ||
      actionType === appConstants.reportActionTypes.CancelReport
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Method to handle setcontext response
   * Scenarios handled
   *  - publish ReportOpened event to enable/disable performance hook if launch success
   *  - PowerScribe forceminimize request status
   *  - PowerScribe startup and login status
   *  - Report save and close status
   *  - Open report success
   *  - PowerScribe startup, login failure
   *  - report save failure during interrupt scenario
   *  - report cancel failure
   *  - open report failure
   */
  handleSetcontextResponse(response) {
    // Success response received from PSPlugin.
    if (response.resultValue === 'SUCCESS' && !this.reportSupportService.getApplicationTerminateStatus()) {
      log.info(
        'handleSetcontextResponse - Success response received from PSPlugin, ' + 'accession - ' + response.accession
      );
      this.handleSuccessSetcontextResponse(response);
    }
    if (response.resultValue === 'FAILURE' && !this.reportSupportService.getApplicationTerminateStatus()) {
      log.error(
        'handleSetcontextResponse - Failure response received from PSPlugin, ' + 'accession - ' + response.accession
      );

      if (!this.integrationStatusService.getReportingPluginStatus()) {
        return;
      }
      this.handleFailureSetcontextResponse(response);
    }
  }

  /**
   * Method to handle load response
   */
  handlePluginLoadResponse(response) {
    if (response.resultValue === 'SUCCESS') {
      // Terminate PowerScribe after LineShaft restart to prevent out of context issues.
      if (this.reportSupportService.getLineShaftTerminateStatus()) {
        log.info('handlePluginLoadResponse - Terminating PowerScribe to prevent out of context issues');
        const psTerminateXml = this.xmlTemplateManagerService.getTerminateXml(
          this.REPORTING_CONFIG.OWNERINSTANCE,
          this.reportingAppProcessName
        );
        this.diService.invokeAPIWrapperExecute(psTerminateXml);
        this.reportSupportService.setLineShaftTerminateStatus(false);
      }

      log.info('handlePluginLoadResponse : PSPlugin load success. ' + 'Send request to launch PowerScribe');
      this.integrationStatusService.setReportingPluginStatus(true);
      // Create the reporting launch xml and launch PowerScribe
      const isUserNameRequired = true;
      const state = this.reportSupportService.getAutoMinimizePreference()
        ? appConstants.windowState.Minimize
        : appConstants.windowState.NoStateChange;

      const systemProps = this.utilService.getSystemProp();
      const isSSOFlagAbsentOrFalse = !systemProps['SSO_Flag'] || systemProps['SSO_Flag'] === 'false';

      if (this.diConfigService.getReportingConfig().SENDPASSWORD === 'true' && isSSOFlagAbsentOrFalse) {
        const reportingSetConfigKeyXML = this.xmlTemplateManagerService.getPSLaunchXml(
          state,
          false,
          appConstants.reportActionTypes.Minimize,
          this.reportingAppProcessName,
          true
        );

        this.reportSupportService.invokeAPIWrapper(reportingSetConfigKeyXML, undefined);
      }
      if (
        this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] &&
        this.utilService.getSystemProp()['REPORTING_SSO_LOGIN'] === 'true' &&
        this.utilService.getSystemProp()['SSO_Flag'] &&
        this.utilService.getSystemProp()['SSO_Flag'] === 'true'
      ) {
        const reportingSetConfigKeyXML = this.xmlTemplateManagerService.getPSLaunchXml(
          state,
          false,
          appConstants.reportActionTypes.Minimize,
          this.reportingAppProcessName,
          true
        );

        this.reportSupportService.invokeAPIWrapper(reportingSetConfigKeyXML, undefined);
      }

      const reportingLaunchXML = this.xmlTemplateManagerService.getPSLaunchXml(
        state,
        isUserNameRequired,
        appConstants.reportActionTypes.Login,
        this.reportingAppProcessName
      );
      this.reportSupportService.invokeAPIWrapper(reportingLaunchXML, undefined);
      this.dictationStatus = appConstants.dictationIntegrationStatus.LaunchApplicationInitiated;
    }
    if (response.resultValue === 'FAILURE') {
      log.error('handlePluginLoadResponse : PSPlugin load failed');
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      this.integrationStatusService.setReportingPluginStatus(true);
      this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    }
  }

  /**
   * Method to check whether integration is loading or not
   */
  isIntegrationLoading(currentAction) {
    let status = false;
    if (this.dictationStatus === appConstants.dictationIntegrationStatus.LaunchApplicationInitiated) {
      if (currentAction && currentAction !== 'preview' && currentAction !== 'closediscard') {
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.dictationIsInitializing'),
          this.translate.instant('di.desktopIntegration.title')
        );
      }
      status = true;
    }
    return status;
  }

  /**
   * Method to set reporting application state after sending close/clear action.
   */
  resetApplicationState(context) {
    try {
      if (context.id === undefined) {
        log.error('resetApplicationState - workitem id is undefined');
        return;
      }
      this.reportSavedEventHandler(context.id);
    } catch (error) {
      log.error('failed on resetApplicationState with ' + error.description);
    }
  }

  /**
   * Method to launch report.
   */
  launchReport(context) {
    try {
      if (context.id === undefined) {
        log.error('launchReport - workitem id is undefined');
        return;
      }
      if (
        context.id === this.reportContextService.getCurrentRequestedProcId() &&
        this.reportContextService.getReportContext() === context.keys.procedureAccessionNo
      ) {
        log.info('Skip launchReport');
        return;
      }
      const siteNameResult = this.getSiteName(context.keys.siteNameKey);
      this.reportContextService.setNPRContextAuditData(context);
      log.info('launchReport : Open report for accession ' + context.keys.procedureAccessionNo);
      // Per old code dictationRetryEnabled flag reset happens here. But could not find any scenario for which this
      // requires.
      if (siteNameResult.status) {
        // Save and close previous report opened.
        if (this.isPreviousContextPresent(context)) {
          this.reportContextService.setCurrentContext(context.id, '');
          log.info('launchReport - save and close the previous report opened');
          this.setContext(appConstants.windowState.ShowOnTop, appConstants.reportActionTypes.SaveReport, undefined);
        }
        // Open report on launch action
        this.reportContextService.setCurrentContext(
          context.id,
          context.keys.procedureAccessionNo,
          siteNameResult.siteName
        );
        this.reportContextService.resetDiscardFlag();
        PerformanceMonitorService.start(
          PerformanceSource.REPORT,
          context.keys.procedureAccessionNo,
          context.keys.studyClickTime ? new Date(context.keys.studyClickTime) : undefined
        );
        this.setContext(appConstants.windowState.ShowOnTop, appConstants.reportActionTypes.OpenReport, undefined);
      } else {
        this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
      }
    } catch (error) {
      log.error('failed on launchReport event with ' + error.description);
    }
  }

  isPreviousContextPresent(context) {
    return (
      this.reportContextService.getCurrentAccession() &&
      this.reportContextService.getReportContext() &&
      this.reportContextService.getReportContext() !== context.keys.procedureAccessionNo
    );
  }

  previewReport(context) {
    try {
      if (context.id === undefined) {
        log.error('previewReport - workitem id is undefined');
        return;
      }
      const siteNameResult = this.getSiteName(context.keys.siteNameKey);
      log.info('launchPreviewReport : Preview report for accession ' + context.keys.procedureAccessionNo);
      if (siteNameResult.status) {
        // Preview report on 'previewReport' action
        this.reportContextService.setCurrentContext(
          context.id,
          context.keys.procedureAccessionNo,
          siteNameResult.siteName
        );
        this.reportContextService.resetDiscardFlag();
        this.setContext(appConstants.windowState.ShowOnTop, appConstants.reportActionTypes.PreviewReport, undefined);
      } else {
        this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.ForceMinimize, undefined);
      }
    } catch (error) {
      log.error('failed on launchPreviewReport event with ' + error.description);
    }
  }

  /**
   * Method to handle launchDictation action.
   */
  launchDictation(context) {
    this.launchReport(context);
  }

  /**
   * Method to handle read action
   */
  read(context) {
    this.launchDictation(context);
  }

  /**
   * Method to handle preview action
   */
  preview(context) {
    const studyId = context.id;
    log.info('handle preview for studyId : ' + studyId);
    if (studyId === this.reportContextService.getCurrentRequestedProcId()) {
      log.info('handle preview studyId : ' + studyId + ' ; study is already open');
      return;
    }

    this.reportContextService.setCurrentContext(studyId, '');
    if (this.reportContextService.getCurrentRequestedProcId() === '') {
      if (studyId === appConstants.externalTabId) {
        this.setContext(appConstants.windowState.ShowOnTop, appConstants.reportActionTypes.SaveReport, undefined);
      } else {
        if (this.externalContextEnabled) {
          this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.SaveReport, undefined);
        } else {
          // If report context is present send SaveReport action else PreviewReport will handle the state changes
          if (this.reportContextService.getReportContext() !== '') {
            this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.SaveReport, undefined);
          }
          this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.PreviewReport, undefined);
        }
      }
    }
  }

  saveCurrentExam(context?) {
    log.info('handle preview for studyId : ' + context.id);
    this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.SaveReport, undefined);
  }

  /**
   * Method to handle launch readonly action.
   */
  launchReadonly(context) {
    try {
      log.info('launchreadonly event handler - Study Id : ' + context.id + ' ' + JSON.stringify(context));
      this.preview(context);
    } catch (error) {
      log.error('failed on launchreadonly action handler with ' + error);
    }
  }

  /**
   * Method to handle closediscard action.
   * The responsibility of this method is to:
   *  1. Send SETCONTEXT for PSPlugin to set discard flag.
   *  2. Send SETCONTEXT for PSPlugin to Close the report by passing empty accession number.
   *  3. If reporting context is not present then send hide request to minimize PowerScribe
   */
  closeDiscardReport(context) {
    try {
      log.info('closeDiscard - close request for studyid : ' + context.id);
      if (context.id === undefined) {
        log.error('closeDiscard - workitem id is undefined');
        return;
      }
      if (
        this.reportContextService.checkReportInContext(context.id) &&
        this.reportContextService.getCurrentRequestedProcId() !== context.id
      ) {
        log.info('closeDiscard - close request for a hidden context/inactive tab is received studyid : ' + context.id);
        this.reportContextService.removeContext(context.id);
        return;
      }

      if (!this.reportContextService.checkReportInContext(context.id)) {
        log.debug('closeDiscard : ' + context.id + ' ; Study not in context');
        if (
          this.reportContextService.getCurrentRequestedProcId() === '' &&
          !this.isCancelActionFailed &&
          !this.isSaveActionFailed
        ) {
          log.info('closeDiscard - there is no current context, send hide/minimize request');
          // To minimize/preview PowerScribe on closing the exam.
          if (!this.externalContextEnabled && this.utilService.getTabsLength() > 0) {
            this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.PreviewReport, undefined);
          } else {
            this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
          }
        }
        return;
      }
      this.discardReport(context);
    } catch (error) {
      log.error('failed on closediscard event with ' + error.description);
    }
  }

  /**
   * Method to discard report.
   */
  discardReport(context) {
    if (context) {
      // If report is not opened for current context, skip the discard xml's
      if (context && this.reportContextService.getReportContext() !== context.keys.procedureAccessionNo) {
        log.info('discardReport - skip discard report since no report is opened for current context');
        this.reportContextService.removeContext(context.id);
        // If there is no context in DxHub sending xml to handle PowerScribe state
        if (_.isNull(this.diService.getApplicationContext())) {
          this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.Minimize, undefined);
        }
        return;
      }
    }

    // Keep the accession number of close requested report to handle the close failure
    this.cancelReportAccession = this.reportContextService.getCurrentAccession();

    let actionType;
    // Read save preference only if closediscard action is triggered as part of tab close
    if (
      context &&
      context.keys.source === 'diagnostic-hub-tab' &&
      (context.actionType == 'externalKeep' || context.actionType == 'defaultKeep')
    ) {
      this.reportContextService.setCurrentContext(context.id, '');
      actionType = appConstants.reportActionTypes.SaveOnCloseReport;
    } else {
      // Both in case of dx hub close-discard or take control close-discard
      this.reportContextService.setDiscardFlag();
      // Set discard option for the opened report
      log.info('discardReport - setContext to set discard flag');
      this.setContext(appConstants.windowState.ShowOnTop, appConstants.reportActionTypes.OpenReport, undefined);
      this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
      actionType = appConstants.reportActionTypes.CancelReport;
      this.reportContextService.resetDiscardFlag();
    }
    // Save or discard report based on save preference on tab closure
    log.info('discardReport - setContext to save/discard the report');
    this.setContext(appConstants.windowState.Minimize, actionType, undefined);
  }

  public releaseControl(target: any): Promise<any> {
    if (
      this.reportContextService.getReportContext() === target.accessionNo &&
      this.integrationStatusService.isReportPending()
    ) {
      const tab = this.utilService.getActiveTab();
      if (tab && tab.workItem) {
        this.integrationStatusService.displayPendingNotificationModal(tab.workItem);
      }
      return Promise.reject(target);
    }
    let actionType;
    const id = this.reportContextService.getContextCorrespondingToAccessionNum(target.accessionNo);
    if (
      id !== '' &&
      (target.releaseOption === 'save' || (target.releaseOption === 'auto' && this.isSaveOnReleaseControl()))
    ) {
      this.reportContextService.setCurrentContext(id, '');
      actionType = appConstants.reportActionTypes.SaveOnCloseReport;
      // Save report based on save preference on tab closure
      log.info('saveReport - setContext to save the report');
      this.setContext(appConstants.windowState.Minimize, actionType, undefined);
      return Promise.resolve(target);
    } else if (
      target.releaseOption === 'discard' ||
      (target.releaseOption === 'auto' && !this.isSaveOnReleaseControl())
    ) {
      return Promise.resolve(target);
    }
    return Promise.reject(target);
  }

  // #Added for take control
  private isSaveOnReleaseControl() {
    return this.preferences.getUserPreferenceFromCache('save-report-on-release-control') === 'save';
  }

  /**
   * Method to handle report save or auto save for associated exam
   */
  associatedReportSavedOrClosed(responseInfoParams) {
    const accNumArray = responseInfoParams.accessionInSaveEvent.split(this.commaDelimiter);
    // Re-set any previously made changes.,
    // Desktop Integration clears the current context when the launched associated exam gets interrupted or
    // closed from DxHub. Use previous context information to find the primary exam.
    log.info(
      'associatedReportSavedOrClosed -  currentAcc : ' +
        this.reportContextService.getCurrentAccession() +
        '  prevAccession: ' +
        this.reportContextService.getPreviousAccession() +
        '  Associated Report : ' +
        responseInfoParams.accessionInSaveEvent
    );

    if (responseInfoParams.actionType === appConstants.reportActionTypes.SaveOnCloseReport) {
      // The previous accession will be empty on tab close, so re-using the accession saved in discardReport()
      // function,  which comes in save on tab close flow
      responseInfoParams.accessionInSaveEvent = this.cancelReportAccession;
    } else {
      // Skip REPORT_AUTO_SAVED event if associated exam tab is closed.
      // Associated exams gets handled from closediscard action handler.
      if (
        responseInfoParams.topicName === this.REPORT_AUTO_SAVED_TOPIC &&
        this.reportContextService.getPreviousAccession() === ''
      ) {
        log.info('associatedReportSavedOrClosed - ' + 'associated exam tab is closed. Skip REPORT_AUTO_SAVED event');
        return;
      }
      // Find primary exam from associated exam list
      responseInfoParams.accessionInSaveEvent = this.reportSupportService.findPrimaryExam(
        responseInfoParams.topicName,
        accNumArray
      );
    }

    // Get context ID and then remove context.
    let contextId = this.reportContextService.getContextCorrespondingToAccessionNum(
      responseInfoParams.accessionInSaveEvent
    );
    if (contextId === '') {
      contextId = responseInfoParams.primaryId;
    }
    this.handleAssociatedExams(responseInfoParams, accNumArray, contextId);
    this.reportSavedEventHandler(contextId);
  }

  /**
   * Method to do the cleanup on signout.
   *
   */
  cleanup() {
    try {
      log.info('Start PowerScribe reporting integration cleanup');
      // Set logout initiated status.
      this.diService.setLogoutInitiatedStatus();

      // During logout LineShaft WebSocket connection is closed without any delay. So even if we send the
      // SaveReport request form here it may not be processed by the Plugin. So removing the SaveReport from here,
      // it is already handled in Plugin Close handler which is supposed to execute either from the Close
      // request sending below or from LineShaft WebSocket connection close flow.

      // Request to close PSPlugin should send even if plugin load is failed, so no need to check load
      // status.
      this.sendCloseXML();
      this.cleanupSubscriptions();
    } catch (error) {
      log.error('error during cleanup: ' + error.description);
      return false;
    }
  }

  /**
   * Method sends close xml
   */
  sendCloseXML() {
    const closeXML = this.xmlTemplateManagerService.getPluginCloseXml(this.REPORTING_CONFIG.MODULENAME);
    this.diService.invokeAPIWrapperExecute(closeXML);
  }

  resetIntegration() {
    // Removing the reporting context since application is going to reload.
    this.reportContextService.removeContext(this.reportContextService.getCurrentRequestedProcId());
    this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.Terminate, undefined);
  }

  /**
   * Function to check for LSPluginManager response
   *
   */
  isLSPluginManagerFailureResponse(response) {
    return !!(
      (response.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE' ||
        response.topicName === 'RadDesktop.CTX_CTRL_MESSAGE_RESPONSE') &&
      response.sourceName === 'LineShaftPluginManager' &&
      response.resultValue === 'FAILURE'
    );
  }

  async handleResponseCase(response) {
    switch (response.topicName) {
      case 'LS_PLUGIN_LOAD_RESPONSE':
        this.handlePluginLoadResponse(response);
        break;
      case 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE':
        this.handleSetcontextResponse(response);
        break;
      case this.REPORT_SAVED_TOPIC:
      case this.REPORT_AUTO_SAVED_TOPIC:
        // If bidirectional context setting is in progress, wait until it finishes
        // to prevent report save before report meta data creation.
        if (this.reportSupportService.getBidirectionalPromise()) {
          await this.reportSupportService.getBidirectionalPromise();
        }
        this.reportSavedOrAutoSavedHandler(response);
        break;
      case this.POWERSCRIBE_CLOSE_TOPIC:
        this.powerScribeCloseResponseHandler();
        break;
      case this.REPORT_OPENNED_TOPIC:
        this.reportOpenEventHandler(response);
        break;
      case this.REPORT_ASSOCIATED_TOPIC:
        this.reportAssociationEventHandler(response);
        break;
    }
  }

  /**
   * Function to handle PSPlugin specific LineShaft messages on handleLineShaftMessage event
   *
   */
  handleLineShaftMessage(response) {
    if (this.isLSPluginManagerFailureResponse(response) && !this.diService.isLogoutInitiated()) {
      // Lineshaft crash - disable integration, no need to notify user from child controllers as it is handled in
      // parent controller's LSPluginManagerResponseHandler() function
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      log.error('handleLineShaftMessage -Lineshaft crash, desktop integration disabled');
      this.reportSupportService.clearMessageQueue();
    } else if (response.sourceName === this.REPORTING_CONFIG.MODULENAME) {
      this.handleResponseCase(response);
    }

    this.handlePSPluginResponse(response);
    this.triggerProcessQueueOnResponse(response);
  }

  /**
   * Function to handle the PSPlugin responses
   *
   */
  handlePSPluginResponse(response) {
    if (response.topicName === this.USER_LOGIN_TOPIC || response.topicName === this.OPEN_REPORT_TOPIC) {
      response.loginResponse = response.topicName === this.USER_LOGIN_TOPIC ? true : false;
      // Changing the topic name so as to handle to treat as setcontext response for clearing the queue
      response.topicName = 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE';
      this.handleSetcontextResponse(response);
    } else if (
      response.topicName === this.USER_LOGGEDIN_TOPIC &&
      response.userName.toLowerCase() !== this.appConfigService.getUserId().toLowerCase()
    ) {
      this.reportSupportService.clearMessageQueue();
      this.reportSupportService.clearMessageWaitingForResponse();
      this.differentUserLoginResponseHandler();
    } else if (response.topicName === this.USER_LOGGEDIN_TOPIC) {
      this.handleSetcontextResponse(response);
    }

    if (response.topicName === this.PS_CHILD_WINDOW_TOPIC) {
      log.error('handlePSPluginResponse - Child window present');
      // Dismiss the open report failed modal popup if exist, when we detect a child window over PowerScribe.
      if (this.openReportModalInstance) {
        log.info('Dismissing open report failed modal popup on detecting a child window over PowerScribe.');
        this.openReportModalInstance.close();
        this.openReportModalInstance = undefined;
      }
      this.notify
        .critical(
          this.translate.instant('di.desktopIntegration.critical.childWindowPresent'),
          this.translate.instant('di.desktopIntegration.title')
        )
        .then(() => {
          this.setContext(appConstants.windowState.ShowOnTop, appConstants.reportActionTypes.Minimize, undefined);
        });
    }
    if (response.topicName === this.PS_MODAL_DIALOG_TOPIC) {
      if (response.modalDialog) {
        log.info('handlePSPluginResponse - Modal Dialog Opened');
        this.integrationStatusService.setReportingHealthStatus(
          this.integrationStatusService.healthStatusEnum.pendingResponse
        );
      } else {
        log.info('handlePSPluginResponse - Modal Dialog Closed');
        this.integrationStatusService.setReportingHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
      }
    }
  }

  /**
   * This method is responsible for processing the queued requests on receiving the response.
   *
   * responseInfo - response information
   */
  triggerProcessQueueOnResponse(responseInfo) {
    // If setcontext response is received process the next request from queue
    if (
      (responseInfo.topicName.indexOf('SETCONTEXT_RESPONSE') > 0 &&
        responseInfo.actionStatus !== 'ACTION_INPROGRESS') ||
      responseInfo.topicName.indexOf('CLOSE_RESPONSE') > 0
    ) {
      log.info(
        'triggerProcessQueueOnResponse - setcontext/close response' + ' received process next request from queue'
      );
      const messageWaitingForResponse = this.reportSupportService.getMessageWaitingForResponse();
      if (
        responseInfo.sourceName === this.REPORTING_CONFIG.MODULENAME &&
        messageWaitingForResponse !== undefined &&
        messageWaitingForResponse.accession === responseInfo.accession
      ) {
        log.info('triggerProcessQueueOnResponse - process next request from reporting queue');
        this.reportSupportService.clearMessageWaitingForResponse();
        // Clear the report setcontext timeout
        this.reportSupportService.clearSetContextTimeout();
        this.reportSupportService.processQueue();
      }
    }
  }

  /**
   * The responsibility of this method is to:
   * 1. Display error message when timeout occurs
   * 2. Process next request from report message queue
   */
  handleSetContextTimeout = () => {
    log.info('handleSetContextTimeout : timeout occurred while receiving the report response');
    const messageWaitingForResponse = this.reportSupportService.getMessageWaitingForResponse();
    if (messageWaitingForResponse !== undefined) {
      if (messageWaitingForResponse.accession !== '' && !this.reportContextService.getDiscardFlag()) {
        this.notify.error(
          this.translate
            .instant('di.desktopIntegration.error.openReportResponsePendingOnTimeout')
            .replace(/ACC#/g, messageWaitingForResponse.accession),
          this.translate.instant('di.desktopIntegration.title')
        );
        // Publish Report Opened event with failure result to third party applications
        const report = { accession: messageWaitingForResponse.accession };
        this.publishReportingEvent(REPORTING_EVENT.OPEN, report, false, appConstants.FAILURE);
      } else {
        this.notify.error(
          this.translate.instant('di.desktopIntegration.error.reportResponsePendingOnTimeout'),
          this.translate.instant('di.desktopIntegration.title')
        );
        if (
          this.isSaveCancelFailureResponse(messageWaitingForResponse.action) &&
          this.reportContextService.getReportContext() !== ''
        ) {
          const accession = this.reportContextService.getReportContext();
          const tab = this.utilService.getTabByAccession(accession);
          // Publish Report Closed event with failure result to third party applications
          const report = { accession: accession };
          const tabInterrupted = tab ? (tab.active ? false : true) : false;
          this.publishReportingEvent(REPORTING_EVENT.CLOSE, report, tabInterrupted, appConstants.FAILURE);
        }
        // Clear all the pending requests in report queue.
        this.reportSupportService.clearMessageQueue();
        if (messageWaitingForResponse.accession !== '') {
          log.info(
            'handleSetContextTimeout :  Hide open report on report discard timeout to ' + 'avoid out of context issues'
          );
          // Hide open report on report discard timeout to avoid out of context issues
          this.setContext(appConstants.windowState.Hide, appConstants.reportActionTypes.ForceMinimize, undefined);
        }
        this.reportSupportService.clearMessageWaitingForResponse();
        this.reportSupportService.processQueue();
      }
    }
  };

  /**
   * This method sets the site name key so that the site name can be identified from object mappings.
   * @param key - Site name key.
   */
  getSiteName(key) {
    if (this.REPORTING_CONFIG.SINGLESITE === 'true') {
      log.info('getSiteName - singleSite selected, SiteName : ' + this.REPORTING_CONFIG.SITENAME);
      return { siteName: this.REPORTING_CONFIG.SITENAME, status: true };
    } else {
      let siteName = '';
      let validMultiSiteConfig = true;
      if (this.dictationStatus === appConstants.dictationIntegrationStatus.Enabled) {
        validMultiSiteConfig = this.validateMultiSiteMapConfig();
        if (validMultiSiteConfig) {
          siteName = this.getSiteNameFromMap(key);
        }
      }
      return siteName === '' ? { status: false } : { siteName, status: true };
    }
  }

  getSiteNameFromMap(key) {
    if (_.isUndefined(key)) {
      // Site name  key is not present in ProductionConf, since the key is undefined.
      this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
      log.error('getSiteNameFromMap - site name key location is undefined in configuration');
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.siteNameKeyNotPresentInWorkItem'),
        this.translate.instant('di.desktopIntegration.titleDictateDisabled')
      );
      return '';
    } else if (UtilService.isEmpty(key)) {
      // Site Name key is empty
      log.error('getSiteNameFromMap - site name key is empty - ' + key);
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.emptySiteNameKey'),
        this.translate.instant('di.desktopIntegration.title')
      );
      return '';
    }
    const valueFromKey = this.getValueFromKey(key);
    if (_.isUndefined(valueFromKey)) {
      // site name key not present in map
      log.error('getSiteNameFromMap - site name key is not present in map - ' + this.getValueFromKey(key));
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.invalidSiteNameKey'),
        this.translate.instant('di.desktopIntegration.title')
      );
      return '';
    } else if (
      UtilService.isEmpty(valueFromKey) ||
      valueFromKey === ' ' ||
      valueFromKey === 'null' ||
      valueFromKey === 'NULL'
    ) {
      // valueFromKey is getting as string from CPACS,so added the string comparison. Also empty string is treated
      // as single space, so if insert empty string it will be single space.
      // Empty site name corresponding to the key
      log.error('getSiteNameFromMap - empty site name corresponding to the key - ' + this.getValueFromKey(key));
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.emptySiteName'),
        this.translate.instant('di.desktopIntegration.title')
      );
      return '';
    }
    log.info('getSiteNameFromMap - : ' + valueFromKey);
    return valueFromKey;
  }

  getValueFromKey(key) {
    // In site name map, key is store with prefix 'PS360.'
    // Append prefix to retrieve key valye from site name map.
    key = 'PS360.' + key;
    const domainOrgItem = {};
    const itemToSearch = 'authorityLocalName';
    if (!_.isUndefined(key)) {
      domainOrgItem[itemToSearch] = key.toString();
      if (!_.isUndefined(this.REPORTING_CONFIG.SITENAMEMAP)) {
        const domainOrg = _.findWhere(this.REPORTING_CONFIG.SITENAMEMAP, domainOrgItem);
        log.info('getValueFromKey - ' + JSON.stringify(domainOrg));
        return !_.isUndefined(domainOrg) ? domainOrg.description : undefined;
      }
    }
    log.info('getValueFromKey - return with undefined');
    return undefined;
  }

  /**
   * This method checks the SITENAMEMAP is undefined or empty
   */
  validateMultiSiteMapConfig() {
    const commonConfig = this.diConfigService.DI_CONFIG;
    if (
      this.REPORTING_CONFIG.SITENAMEMAP === undefined ||
      Object.keys(this.REPORTING_CONFIG.SITENAMEMAP).length === 0
    ) {
      this.displayEmptySiteMapConfigError();
      log.error(this.translate.instant('di.desktopIntegration.critical.emptySiteNameMapConfiguration'));
      return false;
    } else {
      const keyLocationArray = this.REPORTING_CONFIG.SITENAME_KEY_LOCATION.split(';');
      if (
        keyLocationArray.length === 3 &&
        (!commonConfig?.ORGANIZATIONMAP || Object.keys(commonConfig.ORGANIZATIONMAP).length === 0)
      ) {
        this.displayEmptySiteMapConfigError();
        log.error(this.translate.instant('di.desktopIntegration.error.emptyOrganizationMapConfiguration'));
        return false;
      }
    }
    log.info(' validateMultiSiteMapConfig - return with true');
    return true;
  }

  /**
   * Displays the empty sitemap configuration related error and sets
   * the required constiables
   */
  displayEmptySiteMapConfigError() {
    log.error('displayEmptySiteMapConfigError - Empty sitename map configuration');
    this.notify.critical(
      this.translate.instant('di.desktopIntegration.critical.emptySiteNameMapConfiguration'),
      this.translate.instant('di.desktopIntegration.titleDictateDisabled')
    );
    this.dictationStatus = appConstants.dictationIntegrationStatus.Disabled;
  }

  saveReportEvent(args) {
    this.saveCurrentExam(args);
    this.integrationStatusService.setReportingHealthStatus(
      this.integrationStatusService.healthStatusEnum.pendingResponse
    );
  }

  examAssociation() {
    const action = 'MINIMIZE';
    const state = 'SHOW_ON_TOP';
    const associatedExams = this.tabContextService.getAssociationListAccNumbers() || '';
    log.info(
      'Exam association from DxHub for PS - Primary: ' +
        this.reportContextService.getCurrentAccession() +
        ', Associated Exams : ' +
        associatedExams +
        ', state : ' +
        state +
        ', actionType : ' +
        action
    );
    const accession = this.reportContextService.getCurrentAccession();
    const siteName =
      this.reportContextService.getSiteName() === undefined ? '' : this.reportContextService.getSiteName();
    const psPluginContext = this.xmlTemplateManagerService.getPSSetContextXml(
      accession,
      associatedExams,
      state,
      siteName,
      false,
      action
    );

    if (this.diService.isLogoutInitiated()) {
      log.info('setContext - logout is initiated send direct xml to plugin ' + 'to avoid request queuing');
      this.diService.invokeAPIWrapperExecute(psPluginContext);
    } else {
      this.reportSupportService.invokeAPIWrapper(psPluginContext, accession);
    }
  }

  resetTabOnReportFailure(acc) {
    const tab = this.utilService.getTabByAccession(acc);
    if (tab) {
      this.reportSupportService.releaseReadLock(tab.id);
      if (!this.externalContextEnabled) {
        this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.PreviewReport, undefined);
      } else {
        this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
      }
    }
  }

  publishReportAssociation(id, associatedAcc) {
    log.debug(
      'associationFromReportingApp - Associated accession#s: ' + associatedAcc + ', RequestedProcedureId:' + id
    );
    this.tabContextService.removeState(id, 'associableExamsOnReportOpen');
    this.messageService.broadcast('associationFromReportingApp', { id: id, accession: associatedAcc });
  }

  private broadcastListeners() {
    this.subscribers = [
      this.messageService.on('saveReport').subscribe(args => {
        this.saveReportEvent(args);
      })
    ];
  }

  /**
   * Method to cleanup event subscriptions
   */
  private cleanupSubscriptions() {
    this.subscribers &&
      this.subscribers.forEach(subscriber => {
        subscriber.unsubscribe();
      });
  }

  /**
   * Method to remove disassociated exam from association list
   */
  removeExamFromAssociation(examId) {
    this.reportSupportService.removeExamFromAssociation(examId).then(response => {
      log.info('removeExamFromAssociation response : ', response);
      if (response?.success) {
        this.messageService.broadcast('removeExamFromAssociation', examId);
      }
    });
  }

  private publishReportingEvent(eventName, report, tabInterrupted, result) {
    const reportStatus = report.status || REPORT_STATUS.UNKNOWN;
    this.reportSupportService.publishReportEvents(eventName, report.accession, reportStatus, tabInterrupted, result);
  }

  /**
   * Send request to PSPlugin to minimize PowerScribe application
   */
  requestToMinimize() {
    this.setContext(appConstants.windowState.Minimize, appConstants.reportActionTypes.Minimize, undefined);
  }
}
